if (!customElements.get('newsletter-modal')) {
  customElements.define(
    'newsletter-modal',
    class NewsletterModal extends ModalElement {
      constructor() {
        super();

        // Prevent popup on Shopify robot challenge page
        if (window.location.pathname === '/challenge' || !theme.cookiesEnabled) {
          return;
        }

        if (!theme.config.isTouch || Shopify.designMode) {
          this.init();
        }
        else {
          new theme.initWhenVisible(theme.utils.throttle(this.init.bind(this)));
        }
      }

      get shouldLock() {
        return true;
      }

      get testMode() {
        return this.getAttribute('data-test-mode') === 'true';
      }

      get delay() {
        return this.hasAttribute('data-delay') ? parseInt(this.getAttribute('data-delay')) : 5;
      }

      get expiry() {
        return this.hasAttribute('data-expiry') ? parseInt(this.getAttribute('data-expiry')) : 30;
      }

      get scrollThreshold() {
        return this.hasAttribute('data-scroll-threshold') ? parseInt(this.getAttribute('data-scroll-threshold')) : 0;
      }

      get exitIntent() {
        return this.getAttribute('data-exit-intent') === 'true';
      }

      get cartTrigger() {
        return this.getAttribute('data-cart-trigger') !== 'false';
      }

      get cookieName() {
        return 'concept:newsletter-popup';
      }

      get submited() {
        return this.querySelector('.alert') !== null;
      }

      init() {
        if (this.initialized) return;
        this.initialized = true;
        this._triggered = false;

        // Open modal if errors or success message exist
        if (this.submited) {
          this.load(1);
          return;
        }

        // Don't show if cookie exists (unless test mode)
        if (!this.testMode && this.getCookie(this.cookieName)) {
          return;
        }

        // Scroll-depth trigger
        if (this.scrollThreshold > 0) {
          this._scrollHandler = this._onScroll.bind(this);
          window.addEventListener('scroll', this._scrollHandler, { passive: true });
        }

        // Exit-intent trigger (desktop only — mouse leaving viewport)
        if (this.exitIntent && !theme.config.isTouch) {
          this._exitHandler = this._onExitIntent.bind(this);
          // Small delay before attaching so we don't catch initial mouse movements
          setTimeout(() => {
            document.addEventListener('mouseout', this._exitHandler);
          }, 3000);
        }

        // Add-to-cart trigger — fires when product is successfully added
        if (this.cartTrigger) {
          this._cartHandler = this._onCartAdd.bind(this);
          document.addEventListener('ajaxProduct:added', this._cartHandler);
        }
      }

      _getScrollPercent() {
        const docHeight = Math.max(
          document.body.scrollHeight,
          document.documentElement.scrollHeight
        );
        const winHeight = window.innerHeight;
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        if (docHeight <= winHeight) return 100;
        return Math.round((scrollTop / (docHeight - winHeight)) * 100);
      }

      _onScroll() {
        if (this._triggered) return;
        if (this._getScrollPercent() >= this.scrollThreshold) {
          this._trigger('scroll');
        }
      }

      _onExitIntent(e) {
        if (this._triggered) return;
        // Only trigger when mouse leaves through the top of the viewport
        if (e.clientY <= 0 && e.relatedTarget == null) {
          this._trigger('exit');
        }
      }

      _onCartAdd() {
        if (this._triggered) return;
        // Short delay so the cart drawer can open first, then show popup after
        setTimeout(() => this._trigger('cart'), 1500);
      }

      _trigger(source) {
        if (this._triggered) return;
        this._triggered = true;
        this._cleanup();
        this.show();
      }

      _cleanup() {
        if (this._scrollHandler) {
          window.removeEventListener('scroll', this._scrollHandler);
          this._scrollHandler = null;
        }
        if (this._exitHandler) {
          document.removeEventListener('mouseout', this._exitHandler);
          this._exitHandler = null;
        }
        if (this._cartHandler) {
          document.removeEventListener('ajaxProduct:added', this._cartHandler);
          this._cartHandler = null;
        }
      }

      // Keep original load method for form submit/error reopens
      load(delay) {
        if (Shopify.designMode) return;
        setTimeout(() => this.show(), delay * 1000);
      }

      afterShow() {
        super.afterShow();
        this.classList.add('show-image');
      }

      afterHide() {
        super.afterHide();
        this.classList.remove('show-image');

        // Remove a cookie in case it was set in test mode
        if (this.testMode) {
          this.removeCookie(this.cookieName);
          return;
        }

        this.setCookie(this.cookieName, this.expiry);
      }

      getCookie(name) {
        const match = document.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`);
        return match ? match[2] : null;
      }

      setCookie(name, expiry) {
        document.cookie = `${name}=true; max-age=${(expiry * 24 * 60 * 60)}; path=/`;
      }

      removeCookie(name) {
        document.cookie = `${name}=; max-age=0`;
      }
    }
  );
}
